import { create } from 'zustand';
import {
  knowledgeService,
  type Article,
  type ArticleStatus,
  type ArticleFilter,
  type CreateArticleDto,
  type UpdateArticleDto,
  type Category,
  type CreateCategoryDto,
  type UpdateCategoryDto,
} from '@services/index';

interface KnowledgeState {
  // Articles data
  articles: Article[];
  article: Article | null;
  featuredArticles: Article[];

  // Status counts (fetched independently of filtered articles)
  statusCounts: Record<string, number>;

  // Categories data
  categories: Category[];
  categoryTree: Category[];
  category: Category | null;

  // Pagination
  total: number;
  page: number;
  limit: number;
  totalPages: number;

  // Filters
  filters: ArticleFilter;

  // Loading states
  isLoading: boolean;
  isLoadingArticle: boolean;
  isLoadingCategories: boolean;
  isSaving: boolean;
  error: string | null;

  // Actions - Articles
  fetchStatusCounts: () => Promise<void>;
  fetchArticles: (filter?: ArticleFilter) => Promise<void>;
  fetchArticle: (id: string) => Promise<void>;
  fetchFeaturedArticles: () => Promise<void>;
  createArticle: (dto: CreateArticleDto) => Promise<Article>;
  updateArticle: (id: string, dto: UpdateArticleDto) => Promise<Article>;
  publishArticle: (id: string) => Promise<Article>;
  unpublishArticle: (id: string) => Promise<Article>;
  archiveArticle: (id: string) => Promise<Article>;
  deleteArticle: (id: string) => Promise<void>;

  // Actions - Categories
  fetchCategories: () => Promise<void>;
  fetchCategoryTree: () => Promise<void>;
  fetchCategory: (id: string) => Promise<void>;
  createCategory: (dto: CreateCategoryDto) => Promise<Category>;
  updateCategory: (id: string, dto: UpdateCategoryDto) => Promise<Category>;
  deleteCategory: (id: string) => Promise<void>;
  reorderCategories: (items: Array<{ id: string; sortOrder: number }>) => Promise<void>;

  // Filter actions
  setFilters: (filters: ArticleFilter) => void;
  setPage: (page: number) => void;
  setStatusFilter: (status: ArticleStatus | undefined) => void;
  setCategoryFilter: (categoryId: string | undefined) => void;

  // Reset actions
  reset: () => void;
  resetArticle: () => void;
  resetCategory: () => void;
}

const initialState = {
  articles: [],
  article: null,
  featuredArticles: [],
  statusCounts: {},
  categories: [],
  categoryTree: [],
  category: null,
  total: 0,
  page: 1,
  limit: 20,
  totalPages: 0,
  filters: {},
  isLoading: false,
  isLoadingArticle: false,
  isLoadingCategories: false,
  isSaving: false,
  error: null,
};

export const useKnowledgeStore = create<KnowledgeState>()((set, get) => ({
  ...initialState,

  // ============== ARTICLE ACTIONS ==============

  fetchStatusCounts: async () => {
    try {
      const [draft, published, archived] = await Promise.all([
        knowledgeService.getArticles({ status: 'draft' as ArticleStatus, limit: 1 }),
        knowledgeService.getArticles({ status: 'published' as ArticleStatus, limit: 1 }),
        knowledgeService.getArticles({ status: 'archived' as ArticleStatus, limit: 1 }),
      ]);
      set({
        statusCounts: {
          draft: draft.total,
          published: published.total,
          archived: archived.total,
        },
      });
    } catch (error) {
      console.error('Failed to fetch status counts:', error);
    }
  },

  fetchArticles: async (filter) => {
    const currentFilters = filter || get().filters;
    set({ isLoading: true, error: null, filters: currentFilters });

    try {
      const response = await knowledgeService.getArticles({
        ...currentFilters,
        page: currentFilters.page || get().page,
        limit: currentFilters.limit || get().limit,
      });

      set({
        articles: response.data,
        total: response.total,
        page: response.page,
        limit: response.limit,
        totalPages: response.totalPages,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch articles',
        isLoading: false,
      });
    }
  },

  fetchArticle: async (id) => {
    set({ isLoadingArticle: true, error: null });

    try {
      const article = await knowledgeService.getArticle(id);
      set({ article, isLoadingArticle: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch article',
        isLoadingArticle: false,
      });
    }
  },

  fetchFeaturedArticles: async () => {
    try {
      const featuredArticles = await knowledgeService.getFeaturedArticles();
      set({ featuredArticles });
    } catch (error) {
      console.error('Failed to fetch featured articles:', error);
    }
  },

  createArticle: async (dto) => {
    set({ isSaving: true, error: null });

    try {
      const article = await knowledgeService.createArticle(dto);
      set((state) => ({
        articles: [article, ...state.articles],
        total: state.total + 1,
        isSaving: false,
      }));
      return article;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to create article',
        isSaving: false,
      });
      throw error;
    }
  },

  updateArticle: async (id, dto) => {
    set({ isSaving: true, error: null });

    try {
      const updated = await knowledgeService.updateArticle(id, dto);
      set((state) => ({
        articles: state.articles.map((a) => (a.id === id ? updated : a)),
        article: state.article?.id === id ? updated : state.article,
        isSaving: false,
      }));
      return updated;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update article',
        isSaving: false,
      });
      throw error;
    }
  },

  publishArticle: async (id) => {
    set({ isSaving: true, error: null });

    try {
      const updated = await knowledgeService.publishArticle(id);
      set((state) => ({
        articles: state.articles.map((a) => (a.id === id ? updated : a)),
        article: state.article?.id === id ? updated : state.article,
        isSaving: false,
      }));
      get().fetchStatusCounts();
      return updated;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to publish article',
        isSaving: false,
      });
      throw error;
    }
  },

  unpublishArticle: async (id) => {
    set({ isSaving: true, error: null });

    try {
      const updated = await knowledgeService.unpublishArticle(id);
      set((state) => ({
        articles: state.articles.map((a) => (a.id === id ? updated : a)),
        article: state.article?.id === id ? updated : state.article,
        isSaving: false,
      }));
      get().fetchStatusCounts();
      return updated;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to unpublish article',
        isSaving: false,
      });
      throw error;
    }
  },

  archiveArticle: async (id) => {
    set({ isSaving: true, error: null });

    try {
      const updated = await knowledgeService.archiveArticle(id);
      set((state) => ({
        articles: state.articles.map((a) => (a.id === id ? updated : a)),
        article: state.article?.id === id ? updated : state.article,
        isSaving: false,
      }));
      get().fetchStatusCounts();
      return updated;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to archive article',
        isSaving: false,
      });
      throw error;
    }
  },

  deleteArticle: async (id) => {
    set({ isSaving: true, error: null });

    try {
      await knowledgeService.deleteArticle(id);
      set((state) => ({
        articles: state.articles.filter((a) => a.id !== id),
        total: state.total - 1,
        article: state.article?.id === id ? null : state.article,
        isSaving: false,
      }));
      get().fetchStatusCounts();
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete article',
        isSaving: false,
      });
      throw error;
    }
  },

  // ============== CATEGORY ACTIONS ==============

  fetchCategories: async () => {
    set({ isLoadingCategories: true });

    try {
      const categories = await knowledgeService.getCategories({ isActive: true });
      set({ categories, isLoadingCategories: false });
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      set({ isLoadingCategories: false });
    }
  },

  fetchCategoryTree: async () => {
    set({ isLoadingCategories: true });

    try {
      const categoryTree = await knowledgeService.getCategoryTree();
      set({ categoryTree, isLoadingCategories: false });
    } catch (error) {
      console.error('Failed to fetch category tree:', error);
      set({ isLoadingCategories: false });
    }
  },

  fetchCategory: async (id) => {
    try {
      const category = await knowledgeService.getCategory(id);
      set({ category });
    } catch (error) {
      console.error('Failed to fetch category:', error);
    }
  },

  createCategory: async (dto) => {
    set({ isSaving: true, error: null });

    try {
      const category = await knowledgeService.createCategory(dto);
      // Refresh category tree after creation
      await get().fetchCategoryTree();
      set({ isSaving: false });
      return category;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to create category',
        isSaving: false,
      });
      throw error;
    }
  },

  updateCategory: async (id, dto) => {
    set({ isSaving: true, error: null });

    try {
      const updated = await knowledgeService.updateCategory(id, dto);
      // Refresh category tree after update
      await get().fetchCategoryTree();
      set({
        category: get().category?.id === id ? updated : get().category,
        isSaving: false,
      });
      return updated;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update category',
        isSaving: false,
      });
      throw error;
    }
  },

  deleteCategory: async (id) => {
    set({ isSaving: true, error: null });

    try {
      await knowledgeService.deleteCategory(id);
      // Refresh category tree after deletion
      await get().fetchCategoryTree();
      set({
        category: get().category?.id === id ? null : get().category,
        isSaving: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete category',
        isSaving: false,
      });
      throw error;
    }
  },

  reorderCategories: async (items) => {
    set({ isSaving: true, error: null });

    try {
      await knowledgeService.updateCategorySortOrder(items);
      // Refresh category tree after reorder
      await get().fetchCategoryTree();
      set({ isSaving: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to reorder categories',
        isSaving: false,
      });
      throw error;
    }
  },

  // ============== FILTER ACTIONS ==============

  setFilters: (filters) => {
    set({ filters, page: 1 });
    get().fetchArticles({ ...filters, page: 1 });
  },

  setPage: (page) => {
    set({ page });
    get().fetchArticles({ ...get().filters, page });
  },

  setStatusFilter: (status) => {
    const filters = { ...get().filters, status };
    set({ filters, page: 1 });
    get().fetchArticles({ ...filters, page: 1 });
  },

  setCategoryFilter: (categoryId) => {
    const filters = { ...get().filters, categoryId };
    set({ filters, page: 1 });
    get().fetchArticles({ ...filters, page: 1 });
  },

  // ============== RESET ACTIONS ==============

  reset: () => {
    set(initialState);
  },

  resetArticle: () => {
    set({ article: null, isLoadingArticle: false });
  },

  resetCategory: () => {
    set({ category: null });
  },
}));
